export type QualityLevel = 'low' | 'medium' | 'high';

export interface QualityProfile {
  level: QualityLevel;
  label: string;
  /** Obergrenze fuer devicePixelRatio. */
  maxPixelRatio: number;
  shadows: boolean;
  shadowMapSize: number;
  /** Sichtweite der Kamera in Metern. */
  farPlane: number;
  antialias: boolean;
  /** Anzahl gleichzeitig lebender Partikel (Budget fuer M7). */
  particleBudget: number;
  /** Untergrenze der dynamischen Aufloesungsskalierung. */
  minRenderScale: number;
}

export const QUALITY_PROFILES: Record<QualityLevel, QualityProfile> = {
  low: {
    level: 'low',
    label: 'NIEDRIG',
    maxPixelRatio: 1,
    shadows: false,
    shadowMapSize: 512,
    farPlane: 350,
    antialias: false,
    particleBudget: 60,
    minRenderScale: 0.55,
  },
  medium: {
    level: 'medium',
    label: 'MITTEL',
    maxPixelRatio: 1.5,
    shadows: true,
    shadowMapSize: 1024,
    farPlane: 550,
    antialias: false,
    particleBudget: 160,
    minRenderScale: 0.65,
  },
  high: {
    level: 'high',
    label: 'HOCH',
    maxPixelRatio: 2,
    shadows: true,
    shadowMapSize: 2048,
    farPlane: 900,
    antialias: true,
    particleBudget: 320,
    minRenderScale: 0.75,
  },
};

interface NavigatorWithHints extends Navigator {
  deviceMemory?: number;
}

/**
 * Grobe Ersteinschaetzung anhand von Geraete-Hinweisen. Die Feinjustierung
 * uebernimmt danach die dynamische Aufloesungsskalierung im Loop, die
 * zuverlaessiger ist als jeder Startup-Benchmark.
 */
export function detectQuality(): QualityLevel {
  const nav = navigator as NavigatorWithHints;
  const cores = nav.hardwareConcurrency ?? 4;
  const memory = nav.deviceMemory ?? 4;
  const isMobile = /Android|iPhone|iPad|iPod/i.test(nav.userAgent);
  const dpr = window.devicePixelRatio || 1;

  let score = 0;
  score += cores >= 8 ? 2 : cores >= 6 ? 1 : 0;
  score += memory >= 8 ? 2 : memory >= 4 ? 1 : 0;
  score += isMobile ? 0 : 2;
  score += dpr >= 3 ? 1 : 0;

  const gpu = readGpuRenderer();
  if (gpu) {
    // Bekannte schwache Mobil-GPUs nach unten ziehen.
    if (/Mali-G5|Mali-4|Adreno \(TM\) [45]\d\d|PowerVR/i.test(gpu)) score -= 2;
    if (/Apple (A1[2-9]|M\d)|Adreno \(TM\) 7\d\d/i.test(gpu)) score += 2;
  }

  if (score >= 5) return 'high';
  if (score >= 2) return 'medium';
  return 'low';
}

function readGpuRenderer(): string | null {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    if (!gl) return null;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (!ext) return null;
    return gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string;
  } catch {
    return null;
  }
}
